import React, { useState, useEffect } from 'react';
import {
  Users,
  Shield,
  Lock,
  Eye,
  Search,
  AlertTriangle,
  Check,
  X,
  Settings,
  Navigation,
  Database,
  FolderKanban,
  UserCog,
  Edit2,
  Trash2,
  Copy
} from 'lucide-react';
import { useAuth } from '../../hooks';
import api from '../../services/api';
import UsersList from '../teams/UsersList/UsersList';
import ErrorBoundary from '../common/ErrorBoundary';
import { PageHeader } from '../common';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  display_name?: string;
  email: string;
  job_title?: string;
  department?: string;
  status: string;
  roles?: string;
  created_at: string;
}

interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  is_admin: boolean;
  requires_project_assignment?: boolean;
  permissions?: string | null;
  nav_visibility?: string | null;
  data_restrictions?: string | null;
  user_count?: number;
}

// Navigation items that can be visibility-controlled
interface NavItem {
  id: string;
  label: string;
  description: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', description: 'Main dashboard and overview' },
  { id: 'clients', label: 'Clients', description: 'Client management and details' },
  { id: 'projects', label: 'Projects', description: 'Project management and tasks' },
  { id: 'team', label: 'Team', description: 'Team workload and capacity' },
  { id: 'admin', label: 'Admin', description: 'Administrator settings and user management' },
];

// Navigation visibility rules by role (matches Navigation.tsx logic)
const getNavVisibility = (roleName: string, isAdmin: boolean): Record<string, boolean> => {
  // Admin roles can see everything
  if (isAdmin) {
    return {
      dashboard: true,
      clients: true,
      projects: true,
      team: true,
      admin: true,
    };
  }

  // Default: all roles can see base nav, but not admin
  return {
    dashboard: true,
    clients: true,
    projects: true,
    team: true,
    admin: false,
  };
};

interface UserPermissions {
  view_client_info: boolean;
  view_finance: boolean;
  view_rates: boolean;
  manage_users: boolean;
  admin_settings: boolean;
}

interface PermissionInfo {
  key: keyof UserPermissions;
  label: string;
  description: string;
  icon: React.ReactNode;
  restrictedFor: string[];
}

export const Administrator: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<'permissions' | 'team-members' | 'roles'>('permissions');
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  // Role editing state
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  // Role form state
  const [roleForm, setRoleForm] = useState({
    display_name: '',
    description: '',
    is_admin: false,
    requires_project_assignment: false,
    nav_visibility: {
      dashboard: true,
      clients: true,
      projects: true,
      team: true,
      admin: false,
    },
    data_restrictions: [] as string[],
  });

  // Permission definitions with descriptions
  const permissionDefinitions: PermissionInfo[] = [
    {
      key: 'view_client_info',
      label: 'View Client Information',
      description: 'Access client contact details, company info, and communication history',
      icon: <Eye className="w-5 h-5" />,
      restrictedFor: ['Contractor']
    },
    {
      key: 'view_finance',
      label: 'View Financial Data',
      description: 'Access budgets, costs, revenue, invoices, and payment information',
      icon: <Lock className="w-5 h-5" />,
      restrictedFor: ['Contractor', 'Client Viewer']
    },
    {
      key: 'view_rates',
      label: 'View Hourly Rates',
      description: 'See hourly rates, project rates, and cost information',
      icon: <Lock className="w-5 h-5" />,
      restrictedFor: ['Contractor', 'Client Viewer']
    },
    {
      key: 'manage_users',
      label: 'Manage Users',
      description: 'Create, edit, and delete users and manage their roles',
      icon: <Users className="w-5 h-5" />,
      restrictedFor: ['Contractor', 'Client Viewer', 'Designer', 'Developer']
    },
    {
      key: 'admin_settings',
      label: 'Admin Settings',
      description: 'Access system configuration and administrative features',
      icon: <Shield className="w-5 h-5" />,
      restrictedFor: ['Contractor', 'Client Viewer', 'Designer', 'Developer', 'Team Lead', 'Project Manager']
    }
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const [usersData, rolesData] = await Promise.all([
        api.get<User[]>('/api/users', token),
        api.get<Role[]>('/api/users/roles/all', token)
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      setError('Failed to load administrator data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRolePermissions = (roleName: string): UserPermissions => {
    const lowerRole = roleName.toLowerCase();

    // Check against each permission
    const permissions: UserPermissions = {
      view_client_info: !['contractor'].includes(lowerRole),
      view_finance: !['contractor', 'client viewer'].includes(lowerRole),
      view_rates: !['contractor', 'client viewer'].includes(lowerRole),
      manage_users: ['admin', 'super admin', 'super administrator', 'administrator', 'project manager'].includes(lowerRole),
      admin_settings: ['admin', 'super admin', 'super administrator', 'administrator'].includes(lowerRole)
    };

    return permissions;
  };

  const filteredUsers = users.filter(u =>
    u.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.roles?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role: string) => {
    const lowerRole = role.toLowerCase();
    if (lowerRole.includes('admin')) return 'bg-purple-100 text-purple-800';
    if (lowerRole.includes('manager')) return 'bg-blue-100 text-blue-800';
    if (lowerRole.includes('contractor')) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Data restriction options
  const dataRestrictionOptions = [
    { id: 'client_info', label: 'Client Information', description: 'Hide client contact details and company info' },
    { id: 'finance', label: 'Financial Data', description: 'Hide budgets, costs, revenue, and invoices' },
    { id: 'rates', label: 'Hourly Rates', description: 'Hide rate information' },
    { id: 'user_management', label: 'User Management', description: 'Prevent managing users' },
    { id: 'admin_settings', label: 'Admin Settings', description: 'Block admin configuration access' },
  ];

  // Open role modal for editing
  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setIsCloning(false);
    setRoleError(null);

    // Parse stored JSON values
    let navVis = { dashboard: true, clients: true, projects: true, team: true, admin: role.is_admin };
    let dataRes: string[] = [];

    try {
      if (role.nav_visibility) {
        navVis = typeof role.nav_visibility === 'string' ? JSON.parse(role.nav_visibility) : role.nav_visibility;
      }
      if (role.data_restrictions) {
        dataRes = typeof role.data_restrictions === 'string' ? JSON.parse(role.data_restrictions) : role.data_restrictions;
      }
    } catch (e) {
      console.error('Error parsing role settings:', e);
    }

    setRoleForm({
      display_name: role.display_name,
      description: role.description || '',
      is_admin: role.is_admin,
      requires_project_assignment: role.requires_project_assignment || false,
      nav_visibility: navVis,
      data_restrictions: dataRes,
    });
    setShowRoleModal(true);
  };

  // Open role modal for cloning
  const handleCloneRole = (role: Role) => {
    setSelectedRole(role);
    setIsCloning(true);
    setRoleError(null);

    // Parse stored JSON values
    let navVis = { dashboard: true, clients: true, projects: true, team: true, admin: role.is_admin };
    let dataRes: string[] = [];

    try {
      if (role.nav_visibility) {
        navVis = typeof role.nav_visibility === 'string' ? JSON.parse(role.nav_visibility) : role.nav_visibility;
      }
      if (role.data_restrictions) {
        dataRes = typeof role.data_restrictions === 'string' ? JSON.parse(role.data_restrictions) : role.data_restrictions;
      }
    } catch (e) {
      console.error('Error parsing role settings:', e);
    }

    setRoleForm({
      display_name: `Copy of ${role.display_name}`,
      description: role.description || '',
      is_admin: role.is_admin,
      requires_project_assignment: role.requires_project_assignment || false,
      nav_visibility: navVis,
      data_restrictions: dataRes,
    });
    setShowRoleModal(true);
  };

  // Save role (create or update)
  const handleSaveRole = async () => {
    if (!roleForm.display_name.trim()) {
      setRoleError('Display name is required');
      return;
    }

    setRoleSaving(true);
    setRoleError(null);

    try {
      const payload = {
        display_name: roleForm.display_name,
        description: roleForm.description,
        is_admin: roleForm.is_admin,
        requires_project_assignment: roleForm.requires_project_assignment,
        nav_visibility: roleForm.nav_visibility,
        data_restrictions: roleForm.data_restrictions,
      };

      if (isCloning) {
        // Create new role with a generated name
        const systemName = roleForm.display_name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        await api.post('/api/users/roles', { ...payload, name: systemName }, token || undefined);
      } else if (selectedRole) {
        // Update existing role
        await api.put(`/api/users/roles/${selectedRole.id}`, payload, token || undefined);
      }

      // Refresh roles list
      const updatedRoles = await api.get<Role[]>('/api/users/roles/all', token || undefined);
      setRoles(updatedRoles);
      setShowRoleModal(false);
    } catch (err: any) {
      console.error('Failed to save role:', err);
      setRoleError(err.message || 'Failed to save role');
    } finally {
      setRoleSaving(false);
    }
  };

  // Delete role
  const handleDeleteRole = async (role: Role) => {
    if (!window.confirm(`Are you sure you want to delete the "${role.display_name}" role? This cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/api/users/roles/${role.id}`, token || undefined);
      const updatedRoles = await api.get<Role[]>('/api/users/roles/all', token || undefined);
      setRoles(updatedRoles);
    } catch (err: any) {
      console.error('Failed to delete role:', err);
      window.alert(err.message || 'Failed to delete role');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading administrator panel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg border border-red-200 p-6 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader
        title="Administrator"
        subtitle="Manage team members, roles, and access permissions"
      />

      {/* Tabs */}
      <div className="mx-6 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-6 py-3">
            <div className="flex gap-6 border-b border-gray-200 -mb-px">
              <button
                onClick={() => setActiveTab('team-members')}
                className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'team-members'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Team Members
                </span>
              </button>
              <button
                onClick={() => setActiveTab('permissions')}
                className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'permissions'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Access Rules
                </span>
              </button>
              <button
                onClick={() => setActiveTab('roles')}
                className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'roles'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <UserCog className="w-4 h-4" />
                  Roles
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-6 pb-6">
        {activeTab === 'team-members' && <UsersList />}

        {activeTab === 'roles' && (
          <div>
            {/* Role Management Section */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Role Management</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Manage user roles and their permissions. Roles determine what users can access and do in the system.
                    </p>
                  </div>
                </div>
              </div>

              {/* Roles Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        System Name
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Admin
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Project Assignment
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {roles.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          No roles found
                        </td>
                      </tr>
                    ) : (
                      roles.map((role) => (
                        <tr key={role.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                role.is_admin ? 'bg-purple-100' : 'bg-gray-100'
                              }`}>
                                <UserCog className={`w-4 h-4 ${role.is_admin ? 'text-purple-600' : 'text-gray-600'}`} />
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{role.display_name}</div>
                                {role.description && (
                                  <div className="text-xs text-gray-500 max-w-xs truncate">{role.description}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">{role.name}</code>
                          </td>
                          <td className="px-6 py-4 text-center">
                            {role.is_admin ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                                <Check className="w-3 h-3" /> Yes
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">No</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            {role.requires_project_assignment ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                <AlertTriangle className="w-3 h-3" /> Required
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                All Access
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                              {role.user_count || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleCloneRole(role)}
                                className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                                title="Clone role"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditRole(role)}
                                className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                                title="Edit role"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              {!['super_admin', 'admin'].includes(role.name) && (role.user_count || 0) === 0 && (
                                <button
                                  onClick={() => handleDeleteRole(role)}
                                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Delete role"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Info Footer */}
              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-start gap-2 text-xs text-gray-500">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>
                    Built-in admin roles (Super Admin, Admin) cannot be deleted. Roles with assigned users must have all users reassigned before deletion.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'permissions' && (
          <div>
            {/* Navigation Access Rules */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Navigation className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">Navigation Access</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Shows which navigation items each role can access. Roles marked with "(assigned only)" can only see projects and clients they are assigned to.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Navigation Item
                      </th>
                      {roles.map((role) => (
                        <th key={role.id} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 min-w-[120px]">
                          <div className="flex flex-col items-center">
                            <span>{role.display_name}</span>
                            {role.requires_project_assignment && (
                              <span className="text-[10px] text-amber-600 font-normal normal-case mt-0.5">(assigned only)</span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {NAV_ITEMS.map((navItem) => (
                      <tr key={navItem.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{navItem.label}</div>
                          <div className="text-xs text-gray-500">{navItem.description}</div>
                        </td>
                        {roles.map((role) => {
                          const visibility = getNavVisibility(role.name, role.is_admin);
                          const hasAccess = visibility[navItem.id];
                          return (
                            <td key={role.id} className="px-4 py-3 text-center">
                              {hasAccess ? (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100">
                                  <Check className="w-4 h-4 text-green-600" />
                                </span>
                              ) : (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100">
                                  <X className="w-4 h-4 text-red-600" />
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <span>
                  Navigation visibility is currently read-only. Future updates will allow editing these rules directly from this interface.
                </span>
              </div>
            </div>

            {/* Data Visibility Rules */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">Data Visibility Rules</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Shows what data types each role can access. A red X indicates the role cannot see that type of data.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Data Type
                      </th>
                      {roles.map((role) => (
                        <th key={role.id} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 min-w-[120px]">
                          {role.display_name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {permissionDefinitions.map((perm) => (
                      <tr key={perm.key} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-indigo-600">{perm.icon}</span>
                            <div>
                              <div className="font-medium text-gray-900">{perm.label}</div>
                              <div className="text-xs text-gray-500">{perm.description}</div>
                            </div>
                          </div>
                        </td>
                        {roles.map((role) => {
                          const isRestricted = perm.restrictedFor.some(
                            r => r.toLowerCase() === role.display_name.toLowerCase() ||
                                 r.toLowerCase().replace(' ', '_') === role.name.toLowerCase()
                          );
                          return (
                            <td key={role.id} className="px-4 py-3 text-center">
                              {!isRestricted ? (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100">
                                  <Check className="w-4 h-4 text-green-600" />
                                </span>
                              ) : (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100">
                                  <X className="w-4 h-4 text-red-600" />
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <span>
                  Data visibility is currently read-only and determined by role assignments. Future updates will allow customizing these rules per role.
                </span>
              </div>
            </div>

            {/* Project Assignment Rules */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <FolderKanban className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">Project Assignment Rules</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Shows which roles see all projects/clients vs only their assigned ones. Roles with "Assigned Only" can only view projects they are explicitly assigned to via the project team.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roles.map((role) => (
                  <div
                    key={role.id}
                    className={`border rounded-lg p-4 ${
                      role.requires_project_assignment
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-green-200 bg-green-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 ${role.requires_project_assignment ? 'text-amber-600' : 'text-green-600'}`}>
                        {role.requires_project_assignment ? (
                          <AlertTriangle className="w-5 h-5" />
                        ) : (
                          <Check className="w-5 h-5" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 text-sm">{role.display_name}</h3>
                        <p className={`text-xs mt-1 ${role.requires_project_assignment ? 'text-amber-700' : 'text-green-700'}`}>
                          {role.requires_project_assignment
                            ? 'Assigned projects only'
                            : 'Can view all projects'}
                        </p>
                        {role.requires_project_assignment && (
                          <p className="text-xs text-gray-500 mt-2">
                            This role can only see projects and clients they are assigned to through the project team.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <span>
                  Project assignment rules are stored in the database and can be modified by updating the role's "requires_project_assignment" setting.
                </span>
              </div>
            </div>

      {/* Users List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
            <span className="text-sm text-gray-500">{filteredUsers.length} users</span>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search users by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* User Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role(s)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((u) => {
                const userRoles = u.roles?.split(',') || [];
                const primaryRole = userRoles[0] || 'No role';
                const permissions = getRolePermissions(primaryRole);

                return (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full bg-indigo-100">
                          <span className="text-indigo-700 font-medium text-sm">
                            {u.first_name?.[0]}{u.last_name?.[0]}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {u.first_name} {u.last_name}
                          </div>
                          <div className="text-sm text-gray-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {userRoles.map((role, index) => (
                          <span key={index} className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(role)}`}>
                            {role.trim()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {u.department || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(u.status)}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex gap-2">
                        {permissions.view_client_info ? (
                          <span className="inline-flex items-center gap-1 text-green-600" title="Can view client info">
                            <Check className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600" title="Cannot view client info">
                            <X className="w-4 h-4" />
                          </span>
                        )}
                        {permissions.view_finance ? (
                          <span className="inline-flex items-center gap-1 text-green-600" title="Can view finance">
                            <Check className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600" title="Cannot view finance">
                            <X className="w-4 h-4" />
                          </span>
                        )}
                        <button
                          onClick={() => {
                            setSelectedUser(u);
                            setShowPermissionsModal(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 font-medium"
                        >
                          View All
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Permissions for {selectedUser.first_name} {selectedUser.last_name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Role: {selectedUser.roles || 'No role assigned'}
                  </p>
                </div>
                <button
                  onClick={() => setShowPermissionsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {(() => {
                const userRoles = selectedUser.roles?.split(',') || [];
                const primaryRole = userRoles[0] || 'No role';
                const permissions = getRolePermissions(primaryRole);

                return (
                  <div className="space-y-4">
                    {permissionDefinitions.map((perm) => {
                      const hasPermission = permissions[perm.key];
                      return (
                        <div key={perm.key} className={`border rounded-lg p-4 ${hasPermission ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                          <div className="flex items-start gap-3">
                            <div className={hasPermission ? 'text-green-600' : 'text-red-600'}>
                              {hasPermission ? <Check className="w-6 h-6" /> : <X className="w-6 h-6" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-gray-900">{perm.label}</h4>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${hasPermission ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {hasPermission ? 'Allowed' : 'Restricted'}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">{perm.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p>
                  Permissions are determined by user roles. To change a user's permissions, assign them a different role or contact your system administrator.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Important: Contractor Restrictions</p>
            <p>
              Contractors are restricted from viewing client information and all financial data. This ensures sensitive business information remains confidential while allowing contractors to complete their assigned tasks.
            </p>
          </div>
        </div>
      </div>
          </div>
        )}
        </div>

      {/* Role Edit Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {isCloning ? 'Clone Role' : 'Edit Role'}
                </h3>
                <button
                  onClick={() => setShowRoleModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              {selectedRole && !isCloning && (
                <p className="text-sm text-gray-500 mt-1">
                  Editing: <code className="bg-gray-100 px-1 rounded">{selectedRole.name}</code>
                </p>
              )}
            </div>

            <div className="p-6 space-y-6">
              {roleError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {roleError}
                </div>
              )}

              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Basic Information</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={roleForm.display_name}
                    onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="e.g., Senior Designer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={roleForm.description}
                    onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={2}
                    placeholder="Brief description of this role..."
                  />
                </div>
              </div>

              {/* Role Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Role Settings</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={roleForm.is_admin}
                      onChange={(e) => setRoleForm({
                        ...roleForm,
                        is_admin: e.target.checked,
                        nav_visibility: e.target.checked
                          ? { ...roleForm.nav_visibility, admin: true }
                          : roleForm.nav_visibility
                      })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Administrator Role</div>
                      <div className="text-sm text-gray-500">Has full access to all features and data</div>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={roleForm.requires_project_assignment}
                      onChange={(e) => setRoleForm({ ...roleForm, requires_project_assignment: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Requires Project Assignment</div>
                      <div className="text-sm text-gray-500">Can only see projects and clients they are assigned to</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Navigation Visibility */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Navigation Access</h4>
                <p className="text-sm text-gray-500">Select which navigation items this role can access</p>
                <div className="grid grid-cols-2 gap-3">
                  {NAV_ITEMS.map((navItem) => (
                    <label
                      key={navItem.id}
                      className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={roleForm.nav_visibility[navItem.id as keyof typeof roleForm.nav_visibility] || false}
                        onChange={(e) => setRoleForm({
                          ...roleForm,
                          nav_visibility: {
                            ...roleForm.nav_visibility,
                            [navItem.id]: e.target.checked
                          }
                        })}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                        disabled={navItem.id === 'admin' && !roleForm.is_admin}
                      />
                      <div>
                        <div className="font-medium text-gray-900">{navItem.label}</div>
                        <div className="text-xs text-gray-500">{navItem.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Data Restrictions */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Data Restrictions</h4>
                <p className="text-sm text-gray-500">Select which data types to hide from this role</p>
                <div className="space-y-3">
                  {dataRestrictionOptions.map((option) => (
                    <label
                      key={option.id}
                      className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={roleForm.data_restrictions.includes(option.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setRoleForm({
                              ...roleForm,
                              data_restrictions: [...roleForm.data_restrictions, option.id]
                            });
                          } else {
                            setRoleForm({
                              ...roleForm,
                              data_restrictions: roleForm.data_restrictions.filter(r => r !== option.id)
                            });
                          }
                        }}
                        className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                        disabled={roleForm.is_admin}
                      />
                      <div>
                        <div className="font-medium text-gray-900">{option.label}</div>
                        <div className="text-xs text-gray-500">{option.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {roleForm.is_admin && (
                  <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                    Admin roles have full data access. Data restrictions are disabled.
                  </p>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowRoleModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                disabled={roleSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRole}
                disabled={roleSaving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {roleSaving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                {isCloning ? 'Create Role' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
};
